// A3 Durchlaufplanung · Top-N Widget für Abteilung-Detail
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';
import { scoreToBucket } from '../lib/prioScore';

const MODULE_LABELS: Record<string, string> = {
  drueckteile: 'Drückteile', laufraeder: 'Laufräder',
  baugruppen: 'Baugruppen', montage: 'Montage',
};

interface Props {
  abteilungId: number;
  abteilungName?: string;
  limit?: number;
}

export function EmpfohleneReihenfolge({ abteilungId, abteilungName, limit = 5 }: Props) {
  const data = useApi(() => api.planung.abteilung(abteilungId, limit), [abteilungId, limit]);

  if (data.loading) {
    return <div className="p-4 text-sm text-slate-500">Lade Empfehlung …</div>;
  }
  if (!data.data || data.data.data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Empfohlene Reihenfolge</h3>
        <div className="text-sm text-slate-500">Keine offenen Aufträge.</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          Empfohlene Reihenfolge
          {abteilungName && <span className="text-slate-400 font-normal ml-2">· {abteilungName}</span>}
        </h3>
        <Link to={`/werkstatt?abteilung=${abteilungId}`} className="text-xs text-brand hover:underline">
          In Werkstatt öffnen →
        </Link>
      </div>
      <ol className="divide-y divide-slate-100">
        {data.data.data.map((r, i) => {
          const meta = scoreToBucket(r.score);
          return (
            <li key={`${r.modul}:${r.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
              <span className="text-slate-400 font-mono text-xs w-5">{i + 1}</span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded border ${meta.color}`}>
                {meta.icon} {Math.round(r.score)}
              </span>
              <Link to={`/${r.modul}/${r.id}`} className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {r.kunde_name ?? '—'} · <span className="font-normal text-slate-600">{r.bestellnr}</span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  <span className="uppercase font-medium mr-2">{MODULE_LABELS[r.modul] ?? r.modul}</span>
                  {r.bezeichnung ?? '—'}
                </div>
              </Link>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-500">Lieferung</div>
                <div className={`text-sm font-semibold ${
                  (r.days_to_delivery ?? 0) < 0 ? 'text-red-700'
                  : (r.days_to_delivery ?? 99) <= 3 ? 'text-amber-700'
                  : 'text-slate-700'
                }`}>
                  {r.lieferdatum
                    ? new Date(r.lieferdatum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
                    : '—'}
                </div>
                {r.days_to_delivery !== null && r.days_to_delivery !== undefined && (
                  <div className="text-[10px] text-slate-500">
                    {r.days_to_delivery < 0
                      ? `${Math.abs(r.days_to_delivery)}T überfällig`
                      : `in ${r.days_to_delivery}T`}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
